import intersection from "lodash/intersection";
import { Moment } from "moment";
import { useBookingPastDaysLimit } from "PFApp/hooks/use_booking_past_days_limit";
import { utc } from "PFCore/helpers/date";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { useCallback } from "react";

import { BookingDataItem, RepeatDaysData } from "../booking_form.types";
import { calculateRepeatDays } from "./range_item/calculate_repeat_days";
import { getWithTime } from "./use_default_bookings_mapper";

const clampRepeatDays = (repeatDays: RepeatDaysData, start: Moment, end: Moment): RepeatDaysData => {
  const { allowedWeekDays } = calculateRepeatDays({ start, end });

  return {
    allowedWeekDays,
    selectedWeekDays: intersection(repeatDays.selectedWeekDays, allowedWeekDays)
  };
};

export const useClampBookingsToPastDaysLimit = () => {
  const bookingPastDaysLimit = useBookingPastDaysLimit();
  const { formatISODate, formatISODateTime } = useDateFormatter();

  return useCallback(
    (items: BookingDataItem[]): BookingDataItem[] => {
      const earliestAllowedDate = utc().subtract(bookingPastDaysLimit, "days").startOf("day");

      return items.map((item) => {
        const isEndBeforeLimit = utc(item.endDate).isBefore(earliestAllowedDate);
        const isStartBeforeLimit = utc(item.startDate).isBefore(earliestAllowedDate);

        if (isEndBeforeLimit) {
          const today = utc().startOf("day");
          const todayISO = formatISODate(today);

          return {
            ...item,
            startDate: todayISO,
            endDate: todayISO,
            withTime: false,
            ...(item.repeatDays ? { repeatDays: clampRepeatDays(item.repeatDays, today, today) } : {})
          };
        }

        if (!isStartBeforeLimit) {
          return item;
        }

        const startDate = item.withTime
          ? formatISODateTime(earliestAllowedDate)
          : formatISODate(earliestAllowedDate);

        return {
          ...item,
          startDate,
          withTime: getWithTime(startDate, item.endDate),
          ...(item.repeatDays
            ? { repeatDays: clampRepeatDays(item.repeatDays, earliestAllowedDate, utc(item.endDate)) }
            : {})
        };
      });
    },
    [bookingPastDaysLimit, formatISODate, formatISODateTime]
  );
};
